<template>
  <modal titulo="Remover validação do ato" :loading="loading" loading-txt="Removendo a validação...">

    <div class="alert alert-outline-warning py-2 small">
      <i class="fa fa-triangle-exclamation mr-1"></i>
      A validação é a trava que impede a IA de alterar o ato. Ao removê-la, o ato volta a ser
      editável e passa a ser considerado pelas análises e pelas aplicações de sugestões.
      A remoção é registrada na auditoria do ato com o seu usuário, a data/hora e o motivo informado.
    </div>

    <card :titulo="`Atos (${quantidadeSelecionada}/${atos.length})`">
      <card-content>

        <div class="d-flex mb-2" v-if="atos.length > 1">
          <a class="pointer small" @click.prevent="toggleTodos(true)">Selecionar todos</a>
          <span class="text-muted mx-2 small">|</span>
          <a class="pointer small" @click.prevent="toggleTodos(false)">Limpar seleção</a>
        </div>

        <card-table class="table-striped mb-0">
          <tbody>
            <tr v-for="ato in atos" :key="ato.id">
              <td style="width: 40px" class="text-center">
                <i v-if="selecionados[ato.id]" @click="$set(selecionados, ato.id, false)"
                   class="pointer fa-solid fa-circle-check text-primary"></i>
                <i v-else @click="$set(selecionados, ato.id, true)"
                   class="pointer fa-solid fa-circle-dashed"></i>
              </td>
              <td>
                <strong>{{ ato.label }}</strong>
                <div class="text-muted small" v-if="ato.descricao">{{ ato.descricao }}</div>
                <div class="text-danger small" v-if="falhas[ato.id]">
                  <i class="fa fa-circle-exclamation mr-1"></i>{{ falhas[ato.id] }}
                </div>
              </td>
            </tr>
          </tbody>
        </card-table>

      </card-content>
    </card>

    <card titulo="Confirmação">
      <card-content>
        <v-input v-model="form.motivo" type="textarea" :error="validation.motivo"
                 label="Motivo da remoção da validação"
                 placeholder="Descreva por que a validação está sendo removida (registrado na auditoria)" />

        <div class="custom-control custom-checkbox mt-2">
          <input type="checkbox" class="custom-control-input" id="confirmar-remover-validacao" v-model="form.ciente">
          <label class="custom-control-label small" for="confirmar-remover-validacao">
            Confirmo que desejo remover a validação {{ atos.length > 1 ? 'dos atos selecionados' : 'deste ato' }}.
          </label>
        </div>
      </card-content>
    </card>

    <template #footer>
      <button type="button" @click.prevent="cancelar()" class="btn btn-outline-secondary mr-auto">Cancelar</button>
      <v-button :run="confirmar" :desabilitado="!podeConfirmar" class="btn btn-outline-danger">
        <i class="fa fa-unlock mr-1"></i> Remover validação
      </v-button>
    </template>

  </modal>
</template>

<script>
import FrontBusiness from "@/business/FrontBusiness";
import Card from "@/components/Layout/components/Card.vue";
import CardContent from "@/components/Layout/components/CardContent.vue";
import CardTable from "@/components/Layout/components/CardTable.vue";
import Modal from "@/components/Modal.vue";
import AtoBusiness from "@/business/protocolo/AtoBusiness";

/**
 * Confirmação expressa para remover a validação ("destravar") de atos a partir das telas de
 * análise por IA — vale para os dois motores (Análise de Matrícula ITN e análise legada por ato).
 *
 * Recebe os atos candidatos (já validados) e devolve, via closeModal, o resumo
 * {solicitados, removidos, falhas} para o chamador recarregar a tela. A seleção fica aqui: quando
 * é um ato só, ele vem pré-selecionado; em lote, todos vêm marcados e o usuário desmarca o que
 * não quer. Motivo obrigatório — vai para a auditoria de cada ato no backend.
 */
export default {
  name: "ModalRemoverValidacaoAtos",
  components: {Modal, Card, CardContent, CardTable},

  modal: {
    width: 700
  },

  props: {
    // [{id, label, descricao}] — apenas atos VALIDADOS (o chamador filtra).
    atos: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      form: {
        motivo: '',
        ciente: false
      },
      validation: {},
      selecionados: {},
      // Erro por ato devolvido pelo backend na última tentativa (atoId -> mensagem).
      falhas: {}
    };
  },

  created() {
    const selecao = {};
    this.atos.forEach(ato => { selecao[ato.id] = true; });
    this.selecionados = selecao;
  },

  computed: {
    idsSelecionados() {
      return this.atos.filter(ato => this.selecionados[ato.id]).map(ato => ato.id);
    },
    quantidadeSelecionada() {
      return this.idsSelecionados.length;
    },
    podeConfirmar() {
      return this.quantidadeSelecionada > 0 && this.form.ciente && (this.form.motivo || '').trim().length >= 5;
    }
  },

  methods: {
    toggleTodos(valor) {
      this.atos.forEach(ato => this.$set(this.selecionados, ato.id, valor));
    },

    async confirmar() {
      this.validation = {};
      const motivo = (this.form.motivo || '').trim();

      if (motivo.length < 5) {
        this.$set(this.validation, 'motivo', 'Informe o motivo da remoção da validação (mínimo de 5 caracteres)');
        return;
      }

      if (!this.quantidadeSelecionada) {
        FrontBusiness.showError('', 'Selecione ao menos um ato.');
        return;
      }

      this.loading = true;
      try {
        const ids = this.idsSelecionados;
        // Mesmos endpoints da ficha (POST /ato/{id}/desvalidar e /ato/desvalidar-lote) — a única
        // diferença aqui é o motivo obrigatório. O individual lança em erro; o lote devolve resumo.
        let resultado;
        if (ids.length === 1) {
          const alvo = this.atos.find(ato => ato.id === ids[0]);
          await AtoBusiness.desvalidar(ids[0], {descricao: alvo ? alvo.label : null, motivo});
          resultado = {solicitados: 1, removidos: 1, falhas: []};
        } else {
          resultado = await AtoBusiness.desvalidarLote(ids, motivo);
        }

        this.aplicarFalhas(resultado);

        if (resultado.removidos > 0) {
          const msg = resultado.removidos === 1
            ? 'Validação removida. O ato voltou a ser editável.'
            : `${resultado.removidos} ato(s) tiveram a validação removida.`;
          if (resultado.falhas && resultado.falhas.length) {
            FrontBusiness.showWarn('Remoção parcial', `${msg} ${resultado.falhas.length} ato(s) não puderam ser destravados.`);
          } else {
            FrontBusiness.showSuccess('', msg);
          }
        } else {
          FrontBusiness.showError('', 'Nenhum ato teve a validação removida.');
        }

        // Fecha só quando tudo deu certo — havendo falha, o modal permanece aberto mostrando o erro
        // ato a ato para o usuário decidir (o chamador recarrega a tela de qualquer forma ao fechar).
        if (!resultado.falhas || !resultado.falhas.length) {
          FrontBusiness.closeModal(resultado);
        }
      } catch (e) {
        FrontBusiness.showError('Não foi possível remover a validação', e.message);
      } finally {
        this.loading = false;
      }
    },

    // Registra o erro por ato e desmarca os que já foram destravados — assim uma nova tentativa
    // não repete o que deu certo (repetir devolveria "o ato não está validado").
    aplicarFalhas(resultado) {
      const falhas = {};
      (resultado?.falhas || []).forEach(falha => { falhas[falha.atoId] = falha.erro; });
      this.falhas = falhas;
      this.idsSelecionados.forEach(id => {
        if (!falhas[id]) this.$set(this.selecionados, id, false);
      });
    },

    cancelar() {
      FrontBusiness.closeModal();
    }
  }
};
</script>
